import api from '@common/api';
import { uuidRegEx } from '@common/utility/validations';
import { AssetClass, SubscriptionOffer } from '@services/appsync/types';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';

interface PropertiesModelProps {
  properties: SubscriptionOffer[];
  property: SubscriptionOffer | null;
}
interface PropertiesModelActions {
  fetchProperties: (
    investorAccountID: string | null,
  ) => Promise<{ error?: string; success: boolean; properties?: SubscriptionOffer[] }>;
  fetchProperty: (id: string, investorAccountID: string | null) => Promise<SubscriptionOffer | null | undefined>;
  setProperties: (properties: SubscriptionOffer[]) => void;
}
export const PropertiesModel = createStore(
  persist<PropertiesModelProps & PropertiesModelActions>(
    (set) => ({
      fetchProperties: async (investorAccountID) => {
        try {
          const properties = await api.listSubscriptionOffers({
            asset_class: AssetClass.SFR,
            investor_account_id: investorAccountID ?? null,
          });
          if (properties) set({ properties });
          return { properties, success: true };
        } catch {
          return { error: 'Unable to fetch property list', success: false };
        }
      },
      fetchProperty: async (slug, investorAccountID) => {
        if (!slug) return null;

        // Fetch the subscriptionOffer by ID if the slug appears to be a UUID
        const subscriptionOffer = uuidRegEx.test(slug)
          ? await api.getSubscriptionOffer({ id: slug, investor_account_id: investorAccountID ?? null})
          : await api.getSubscriptionOffer({ investor_account_id: investorAccountID ?? null, slug });
        if (subscriptionOffer) set({ property: subscriptionOffer });
        return subscriptionOffer;
      },
      properties: [],
      property: null,
      setProperties: (properties) => {
        set({ properties });
      },
    }),
    {
      getStorage: () => localStorage,
      name: 'properties-model',
    },
  ),
);

export function usePropertiesModel(): PropertiesModelProps & PropertiesModelActions;
export function usePropertiesModel<T>(
  selector: (state: PropertiesModelProps & PropertiesModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function usePropertiesModel<T>(
  selector?: (state: PropertiesModelProps & PropertiesModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(PropertiesModel, selector!, equals);
}
